<template>
	<div class="requisition-preview" @click="handleClick">
		<fieldset class="talentics-fieldset talentics-fieldset-paddings">
			<legend>
				{{ $t('label.requisition-form') }}
				<span
					v-if="requisition.status === 'draft'"
					style="color: red"
					>({{ $t(`status.${requisition.status}`) }})</span
				>
			</legend>
			<div class="requisition-preview__content">
				<!-- Info rows -->
				<div class="requisition-preview__info">
					<div v-if="requestedByUser" class="requisition-preview__row">
						<v-icon small class="requisition-preview__icon">person_outline</v-icon>
						<span class="requisition-preview__label">{{ $t('label.requested-by') }}</span>
						<span class="requisition-preview__value">{{ requestedByUser.name || requestedByUser.email }}</span>
					</div>
					<div v-if="requisition.created" class="requisition-preview__row">
						<v-icon small class="requisition-preview__icon">schedule</v-icon>
						<span class="requisition-preview__label">{{ $t('label.created-date') }}</span>
						<span class="requisition-preview__value">{{ format(new Date(requisition.created), 'dd.MM.yyyy HH:mm') }}</span>
					</div>
					<div class="requisition-preview__row">
						<v-icon small class="requisition-preview__icon">info_outline</v-icon>
						<span class="requisition-preview__label">{{ $t('label.status') }}</span>
						<span
							class="requisition-preview__badge"
							:class="`requisition-preview__badge--${overallStatus}`"
						>
							{{ $t(`status.${overallStatus}`) }}
						</span>
					</div>
					<!-- Deadline -->
					<div v-if="requisition.deadline" class="requisition-preview__row">
						<v-icon small class="requisition-preview__icon">event</v-icon>
						<span class="requisition-preview__label">{{ $t('label.target-close-date') }}</span>
						<span class="requisition-preview__value" :class="deadlineClass">
							{{ format(new Date(requisition.deadline), 'dd.MM.yyyy') }}
						</span>
					</div>

					<!-- Hiring Progress -->
					<div v-if="requisition.number_of_people" class="requisition-preview__row">
						<v-icon small class="requisition-preview__icon">group</v-icon>
						<span class="requisition-preview__label">{{ $t('label.targeted-hire-count') }}</span>
						<span class="requisition-preview__value">
							{{ requisition.hired_count || 0 }} / {{ requisition.number_of_people }}
						</span>
					</div>

					<!-- Progress Bar -->
					<div v-if="requisition.number_of_people" class="requisition-preview__progress-wrap">
						<div class="requisition-preview__progress-bar">
							<div
								class="requisition-preview__progress-fill"
								:class="progressClass"
								:style="`width: ${progressPercent}%`"
							></div>
						</div>
					</div>
				</div>

				<!-- Approval Flow Steps -->
				<div v-if="approvalSteps.length > 0" class="requisition-preview__steps">
					<div class="requisition-preview__steps-header">
						{{ $t('label.approval-flow') }}
					</div>
					<div class="requisition-preview__timeline">
						<div
							v-for="(step, stepIndex) in approvalSteps"
							:key="`step_${stepIndex}`"
							class="requisition-preview__step"
							:class="{ 'requisition-preview__step--last': stepIndex === approvalSteps.length - 1 }"
						>
							<div class="requisition-preview__step-track">
								<div
									class="requisition-preview__step-dot"
									:class="`requisition-preview__step-dot--${getStepStatus(step)}`"
								></div>
								<div
									v-if="stepIndex < approvalSteps.length - 1"
									class="requisition-preview__step-line"
									:class="`requisition-preview__step-line--${getStepStatus(step)}`"
								></div>
							</div>
							<div class="requisition-preview__step-content">
								<div class="requisition-preview__step-label">
									{{ $t('label.step-no', { stepNo: stepIndex + 1 }) }}
								</div>
								<div
									v-for="(approver, approverIndex) in step.approvers"
									:key="`approver_${stepIndex}_${approverIndex}`"
									class="requisition-preview__approver"
								>
									<span class="requisition-preview__approver-name">
										{{ getApproverName(approver) }}
									</span>
									<span
										v-if="getApproverStatusText(approver)"
										class="requisition-preview__badge requisition-preview__badge--sm"
										:class="`requisition-preview__badge--${getApproverStatus(approver)}`"
									>
										{{ getApproverStatusText(approver) }}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</fieldset>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { format } from 'date-fns';

export default {
	name: 'RequisitionPreview',
	data() {
		return {
			format,
		};
	},
	computed: {
		...mapState({
			users: state => state.users.items,
		}),
		requestedByUser() {
			const { requisition, users } = this;
			if (!requisition.requested_by) return null;
			return users.find(
				u => parseInt(u.id, 10) === parseInt(requisition.requested_by, 10),
			);
		},
		approvalSteps() {
			const { requisition } = this;
			if (
				!requisition.approval_flow_json_schema ||
				!Array.isArray(requisition.approval_flow_json_schema)
			) {
				return [];
			}
			return requisition.approval_flow_json_schema;
		},
		progressPercent() {
			const { requisition } = this;
			if (!requisition.number_of_people) return 0;
			const hired = requisition.hired_count || 0;
			return Math.min(Math.round((hired / requisition.number_of_people) * 100), 100);
		},
		progressClass() {
			if (this.progressPercent >= 100) return 'requisition-preview__progress-fill--complete';
			if (this.progressPercent > 0) return 'requisition-preview__progress-fill--partial';
			return '';
		},
		deadlineClass() {
			const { requisition } = this;
			if (!requisition.deadline) return '';
			const now = new Date();
			const deadline = new Date(requisition.deadline);
			const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
			if (daysLeft < 0) return 'requisition-preview__value--overdue';
			if (daysLeft <= 7) return 'requisition-preview__value--approaching';
			return '';
		},
		overallStatus() {
			const { requisition } = this;
			if (!requisition.user_approval_requests || requisition.user_approval_requests.length === 0) {
				return requisition.status || 'draft';
			}
			const requests = requisition.user_approval_requests;
			if (requests.some(r => r.status === 'rejected')) return 'rejected';
			if (requests.every(r => r.status === 'approved')) return 'approved';
			return 'pending';
		},
	},
	props: {
		requisition: {
			type: Object,
			required: true,
		},
		onClick: {
			type: Function,
			required: false,
			default() {
				return () => {};
			},
		},
	},
	methods: {
		handleClick() {
			this.onClick({ requisition_id: this.requisition.id });
		},
		getApprovalRequest(approver) {
			const { requisition } = this;
			if (!requisition.user_approval_requests) return null;
			return requisition.user_approval_requests.find(
				r => r.id === approver.approval_request_id,
			);
		},
		getApproverName(approver) {
			const user = this.users.find(
				u => parseInt(u.id, 10) === parseInt(approver.user_id, 10),
			);
			if (user) return user.name || user.email;
			return this.$t('label.unknown-user');
		},
		isOverriddenApproval(approvalRequest) {
			if (!approvalRequest) return false;
			return (
				approvalRequest.status === 'approved' &&
				approvalRequest.approved_by &&
				approvalRequest.requested_from &&
				parseInt(approvalRequest.approved_by, 10) !==
					parseInt(approvalRequest.requested_from, 10)
			);
		},
		getApproverStatusText(approver) {
			const request = this.getApprovalRequest(approver);
			if (!request) return '';
			if (this.isOverriddenApproval(request)) {
				return this.$t('status.overridden-approval');
			}
			return this.$t(`status.${request.status}`);
		},
		getApproverStatus(approver) {
			const request = this.getApprovalRequest(approver);
			if (!request) return '';
			if (this.isOverriddenApproval(request)) return 'overridden';
			return request.status;
		},
		getStepStatus(step) {
			const { approvers } = step;
			if (!approvers || approvers.length === 0) return 'default';
			const statuses = approvers.map(a => this.getApproverStatus(a));
			if (statuses.some(s => s === 'rejected')) return 'rejected';
			if (statuses.every(s => s === 'approved' || s === 'overridden')) return 'approved';
			if (statuses.some(s => s === 'pending')) return 'pending';
			return 'default';
		},
	},
};
</script>

<style lang="scss" scoped>
.requisition-preview {
	cursor: pointer;
	position: relative;

	&:hover {
		fieldset {
			border-color: #90caf9;
		}
	}

	&__content {
		margin-top: 0.375rem;
	}

	&__info {
		display: flex;
		flex-direction: column;
		gap: 0.375rem;
	}

	&__row {
		display: flex;
		align-items: center;
		gap: 0.375rem;
		font-size: 13px;
		line-height: 1.4;
		overflow: hidden;
	}

	&__icon {
		color: #9e9e9e !important;
		flex-shrink: 0;
	}

	&__label {
		color: #757575;
		white-space: nowrap;
		flex-shrink: 0;

		&::after {
			content: ':';
		}
	}

	&__value {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-weight: 500;
	}

	&__badge {
		display: inline-block;
		font-size: 11px;
		font-weight: 600;
		padding: 1px 8px;
		border-radius: 10px;
		line-height: 1.5;
		white-space: nowrap;

		&--sm {
			font-size: 10px;
			padding: 0 6px;
			line-height: 1.6;
		}

		&--approved {
			background-color: #e8f5e9;
			color: #2e7d32;
		}
		&--rejected {
			background-color: #ffebee;
			color: #c62828;
		}
		&--pending {
			background-color: #fff3e0;
			color: #e65100;
		}
		&--overridden {
			background-color: #e3f2fd;
			color: #1565c0;
		}
		&--draft {
			background-color: #f5f5f5;
			color: #616161;
		}
		&--queued {
			background-color: #e0e0e0;
			color: #424242;
		}
	}

	&__steps {
		margin-top: 0.625rem;
		padding-top: 0.5rem;
		border-top: 1px solid #e0e0e0;
	}

	&__steps-header {
		font-size: 12px;
		font-weight: 500;
		color: #757575;
		margin-bottom: 0.5rem;
	}

	&__timeline {
		padding-left: 0;
	}

	&__step {
		display: flex;
		gap: 0.5rem;
	}

	&__step-track {
		display: flex;
		flex-direction: column;
		align-items: center;
		flex-shrink: 0;
		width: 12px;
		padding-top: 2px;
	}

	&__step-dot {
		width: 10px;
		height: 10px;
		min-height: 10px;
		border-radius: 50%;
		background-color: #bdbdbd;
		flex-shrink: 0;

		&--approved {
			background-color: #4caf50;
		}
		&--rejected {
			background-color: #f44336;
		}
		&--pending {
			background-color: #ff9800;
		}
		&--queued {
			background-color: #757575;
		}
	}

	&__step-line {
		width: 2px;
		flex-grow: 1;
		min-height: 12px;
		background-color: #e0e0e0;

		&--approved {
			background-color: #a5d6a7;
		}
		&--rejected {
			background-color: #ef9a9a;
		}
		&--pending {
			background-color: #ffe0b2;
		}
		&--queued {
			background-color: #bdbdbd;
		}
	}

	&__step-content {
		flex: 1;
		min-width: 0;
		padding-bottom: 0.5rem;
	}

	&__step--last &__step-content {
		padding-bottom: 0;
	}

	&__step-label {
		font-size: 11px;
		font-weight: 500;
		color: #9e9e9e;
		margin-bottom: 0.1875rem;
	}

	&__approver {
		display: flex;
		align-items: center;
		gap: 0.375rem;
		padding: 0.125rem 0;
		font-size: 13px;
		line-height: 1.3;
		overflow: hidden;
		flex-wrap: wrap;
	}

	&__approver-name {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		flex-shrink: 1;
		min-width: 0;
	}

	&__value--overdue {
		color: #c62828;
		font-weight: 600;
	}

	&__value--approaching {
		color: #e65100;
		font-weight: 600;
	}

	&__progress-wrap {
		padding-top: 0.125rem;
	}

	&__progress-bar {
		width: 100%;
		height: 4px;
		background-color: #e0e0e0;
		border-radius: 2px;
		overflow: hidden;
	}

	&__progress-fill {
		height: 100%;
		border-radius: 2px;
		background-color: #e0e0e0;
		transition: width 0.3s ease;

		&--partial {
			background-color: #ff9800;
		}
		&--complete {
			background-color: #4caf50;
		}
	}
}
</style>
